import { useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import {
  BREAKDOWN_ATTRIBUTE_COLUMNS,
  BREAKDOWN_COLUMNS,
  BREAKDOWN_DEFAULT_STAT_COLUMNS,
  BREAKDOWN_STAT_COLUMNS,
  type BreakdownColumn,
  type BreakdownColumnKey,
} from './breakdownColumns';

// The explorer's "Columns" dropdown: a checklist over the catalogue, split into
// Metrics and Attributes (Budget / status / type). The dimension columns are
// driven by the group-by chips, so this governs the metric + attribute columns
// only. Toggling persists via the caller (BreakdownExplorer folds it into the
// reporting-explorer gridPreferences). At least one column always stays on.
export function ExplorerViewMenu({
  visible,
  onChange,
}: {
  visible: readonly BreakdownColumnKey[];
  onChange: (next: BreakdownColumnKey[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const set = new Set(visible);
  const toggle = (key: BreakdownColumnKey) => {
    const next = new Set(set);
    if (next.has(key)) {
      if (next.size === 1) return; // keep at least one column
      next.delete(key);
    } else {
      next.add(key);
    }
    // Preserve catalogue order.
    onChange(BREAKDOWN_COLUMNS.filter((c) => next.has(c.key)).map((c) => c.key));
  };

  const reset = () => onChange([...BREAKDOWN_DEFAULT_STAT_COLUMNS]);

  const section = (title: string, columns: BreakdownColumn[]) => (
    <>
      <div className="px-1 py-1 text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="space-y-0.5">
        {columns.map((c) => {
          const checked = set.has(c.key);
          const lastOne = checked && set.size === 1;
          return (
            <label
              key={c.key}
              className={`flex items-center gap-2 rounded px-1 py-1 ${lastOne ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}
              title={lastOne ? 'At least one column stays visible' : undefined}
            >
              <input type="checkbox" checked={checked} disabled={lastOne} onChange={() => toggle(c.key)} />
              <span className="truncate">{c.label}</span>
            </label>
          );
        })}
      </div>
    </>
  );

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm hover:bg-slate-50"
        title="Choose columns"
      >
        <span className="mr-1 text-xs text-slate-500">Columns</span>
        <span className="font-medium">{visible.length}</span>
        <span className="ml-2 text-slate-400" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 max-h-[70vh] w-64 overflow-y-auto rounded-md border border-slate-300 bg-white p-2 text-sm shadow-lg"
        >
          <div className="mb-1 flex items-center justify-end px-1">
            <button type="button" onClick={reset} className="text-xs text-blue-600 hover:underline">
              Reset to default
            </button>
          </div>
          {section('Metrics', BREAKDOWN_STAT_COLUMNS)}
          <div className="my-1.5 border-t border-slate-100" />
          {section('Attributes', BREAKDOWN_ATTRIBUTE_COLUMNS)}
          <p className="mt-1.5 px-1 text-[11px] leading-tight text-slate-400">
            Attributes (budget/type) show on their own level's rows — best read in
            Tree or a single-level grouping. Delivery status shows as a coloured dot on
            the name. As of last sync.
          </p>
        </div>
      )}
    </div>
  );
}
